import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { Organization } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { LoadingStateType, SliceName } from '@/shared/config';

import { SharedPatientSettings } from '../config';
import { mergeOrganization } from '../lib';

import { setCurrentOrganizationByID } from './organizationSlice.thunks';

export const organizationAdapter = createEntityAdapter<Organization>({
  selectId: (organization) => organization.ID,
});

const initialStateFlags = {};

const initialState = () => ({
  currentOrganizationID: '',
  settingsLoadedForOrganizationID: null as string | null,
  stateFlags: initialStateFlags,
  loading: 'idle' as LoadingStateType,
  sharedPatientSettings: {
    featureFlags: [],
    isSharedPatient: false,
  } as SharedPatientSettings,
  patientOrganization: null as Organization | null,
  storageUsed: '0',
});

const organizationState = organizationAdapter.getInitialState(initialState());

type OrganizationState = typeof organizationState;

const organizationSlice = createSlice({
  name: SliceName.organization,
  initialState: organizationAdapter.getInitialState(initialState()),
  reducers: {
    addOne: (state, action: PayloadAction<Organization>) => {
      organizationAdapter.addOne(state as OrganizationState, action.payload);
    },
    setOne: (state, action: PayloadAction<Organization>) => {
      organizationAdapter.setOne(
        state as OrganizationState,
        mergeOrganization({
          current: state.entities[action.payload.ID] as
            | Organization
            | undefined,
          incoming: action.payload,
        }),
      );
    },
    setNewestOne: (state, action: PayloadAction<Organization>) => {
      if (action.payload.Deleted?.Deleted) {
        organizationAdapter.removeOne(
          state as OrganizationState,
          action.payload.ID,
        );
      }

      organizationAdapter.setOne(
        state as OrganizationState,
        mergeOrganization({
          current: state.entities[action.payload.ID] as
            | Organization
            | undefined,
          incoming: action.payload,
          newestOnly: true,
        }),
      );
    },
    setNewestMany: (state, action: PayloadAction<Organization[]>) => {
      organizationAdapter.setMany(
        state as OrganizationState,
        action.payload.map((item) =>
          mergeOrganization({
            current: state.entities[item.ID] as Organization | undefined,
            incoming: item,
            newestOnly: true,
          }),
        ),
      );
    },
    removeOne: (state, action: PayloadAction<string>) => {
      organizationAdapter.removeOne(state as OrganizationState, action.payload);
    },
    removeAll: (state) => {
      organizationAdapter.removeAll(state as OrganizationState);
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    setPatientOrganization: (
      state,
      action: PayloadAction<Organization | null>,
    ) => {
      state.patientOrganization = action.payload;
    },
    // TODO: deprecated, remove after it's no longer used in MIRenderOrchestrator
    setSharedPatientSettings: (
      state,
      action: PayloadAction<SharedPatientSettings>,
    ) => {
      state.sharedPatientSettings = action.payload;
    },
    setStorageUsed: (state, action: PayloadAction<string>) => {
      state.storageUsed = action.payload;
    },
    setSettingsLoadedForOrganizationID: (
      state,
      action: PayloadAction<string>,
    ) => {
      if (state.currentOrganizationID === action.payload) {
        state.settingsLoadedForOrganizationID = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setCurrentOrganizationByID.fulfilled, (state, action) => {
      state.currentOrganizationID = action.payload;
    });
  },
});

export const { actions } = organizationSlice;

export default organizationSlice.reducer;
